import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@pagespeedify/ui';
import { AlertCircle, CheckCircle2, ChevronDown, Play, Wand2 } from 'lucide-react';
import type { AgentScopeAnswers, PromptScopeSuggestion, PromptSpecialization } from '../../types/agent';

interface ScopeAnswerOptions {
    goal?: string[];
    targetArea?: string[];
    verification?: string[];
}

interface MonitoringSelectionOption {
    id: string;
    websiteUrl: string;
    formFactor?: 'mobile' | 'desktop';
}

interface PromptScopeSuggestionsProps {
    reason?: string;
    specialization?: PromptSpecialization;
    suggestions: PromptScopeSuggestion[];
    requiredHints?: string[];
    onUsePrompt: (suggestion: PromptScopeSuggestion) => void;
    onUseAndRun?: (suggestion: PromptScopeSuggestion) => void;
    disabled?: boolean;
    layoutMode?: 'builder' | 'adaptive' | 'tabs' | 'swipe';
    scopeAnswers?: AgentScopeAnswers;
    scopeAnswerOptions?: ScopeAnswerOptions;
    onScopeAnswersChange?: (answers: AgentScopeAnswers) => void;
    showMonitoringControls?: boolean;
    monitoringOptions?: MonitoringSelectionOption[];
    selectedMonitoringId?: string;
    onSelectMonitoring?: (selection: MonitoringSelectionOption | null) => void;
    memoryDerived?: boolean;
    className?: string;
}

const CATEGORY_STYLES: Record<PromptSpecialization, string> = {
    performance: 'border-blue-500/40 text-blue-700 dark:text-blue-300 bg-blue-500/10',
    security: 'border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10',
    stability: 'border-slate-500/40 text-slate-700 dark:text-slate-300 bg-slate-500/10',
    debugging: 'border-rose-500/40 text-rose-700 dark:text-rose-300 bg-rose-500/10',
    mixed: 'border-border/70 text-muted-foreground bg-muted/30',
};
const CATEGORY_ORDER: PromptSpecialization[] = ['performance', 'security', 'stability', 'debugging'];

function toTitleCase(value: string): string {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function isGoalCategory(value?: string): value is 'performance' | 'security' | 'stability' | 'debugging' {
    if (!value) return false;
    return value === 'performance' || value === 'security' || value === 'stability' || value === 'debugging';
}

function applyScopeAnswersToPrompt(prompt: string, scopeAnswers?: AgentScopeAnswers): string {
    if (!scopeAnswers) return prompt;
    let next = prompt.trim();
    const goal = (scopeAnswers.goal || '').trim();
    const targetArea = (scopeAnswers.targetArea || '').trim();
    const verification = (scopeAnswers.verification || '').trim();

    if (goal && !new RegExp(`\\b${goal.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}\\b`, 'i').test(next)) {
        next = `${next} Focus goal: ${goal}.`;
    }
    if (targetArea && !next.toLowerCase().includes(targetArea.toLowerCase())) {
        next = `${next} Start with ${targetArea}.`;
    }
    if (verification && !next.toLowerCase().includes(verification.toLowerCase())) {
        next = `${next} Verify with ${verification}.`;
    }
    return next;
}

function getCategorySuggestionMap(
    suggestions: PromptScopeSuggestion[]
): Record<PromptSpecialization, PromptScopeSuggestion[]> {
    return suggestions.reduce<Record<PromptSpecialization, PromptScopeSuggestion[]>>(
        (acc, suggestion) => {
            const category: PromptSpecialization = suggestion?.category || 'mixed';
            acc[category].push(suggestion);
            return acc;
        },
        {
            performance: [],
            security: [],
            stability: [],
            debugging: [],
            mixed: []
        }
    );
}

export default function PromptScopeSuggestions({
    reason,
    specialization = 'mixed',
    suggestions,
    requiredHints = [],
    onUsePrompt,
    onUseAndRun,
    disabled = false,
    layoutMode = 'builder',
    scopeAnswers,
    scopeAnswerOptions,
    onScopeAnswersChange,
    showMonitoringControls = false,
    monitoringOptions = [],
    selectedMonitoringId,
    onSelectMonitoring,
    memoryDerived = false,
    className = ''
}: PromptScopeSuggestionsProps) {
    const normalizedSuggestions = useMemo(
        () =>
            (Array.isArray(suggestions) ? suggestions : [])
                .filter((suggestion) => typeof suggestion?.id === 'string' && suggestion.id.trim().length > 0)
                .slice(0, 4),
        [suggestions]
    );
    const suggestionsByCategory = useMemo(
        () => getCategorySuggestionMap(normalizedSuggestions),
        [normalizedSuggestions]
    );
    const availableCategories = useMemo(
        () => CATEGORY_ORDER.filter((category) => (suggestionsByCategory[category] || []).length > 0),
        [suggestionsByCategory]
    );

    const goalOptions = Array.isArray(scopeAnswerOptions?.goal) && scopeAnswerOptions.goal.length > 0
        ? scopeAnswerOptions.goal.filter((goal) => isGoalCategory(goal))
        : ['performance', 'security', 'debugging', 'stability'];
    const targetAreaOptions = Array.isArray(scopeAnswerOptions?.targetArea)
        ? scopeAnswerOptions.targetArea.slice(0, 6)
        : [];
    const verificationOptions = Array.isArray(scopeAnswerOptions?.verification) && scopeAnswerOptions.verification.length > 0
        ? scopeAnswerOptions.verification.slice(0, 6)
        : ['tests', 'build', 'lint', 'smoke'];
    const hasMonitoringOptions = Array.isArray(monitoringOptions) && monitoringOptions.length > 0;

    const [manualSuggestionId, setManualSuggestionId] = useState<string>('');
    const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);
    const [isAlternativesExpanded, setIsAlternativesExpanded] = useState(false);
    const [isHintsExpanded, setIsHintsExpanded] = useState(false);
    const [isMonitoringPickerOpen, setIsMonitoringPickerOpen] = useState(false);
    const [monitoringSearch, setMonitoringSearch] = useState('');
    const [expandedPromptById, setExpandedPromptById] = useState<Record<string, boolean>>({});
    const [internalScopeAnswers, setInternalScopeAnswers] = useState<AgentScopeAnswers>({});
    const effectiveScopeAnswers = scopeAnswers || internalScopeAnswers;
    const selectedMonitoringOption = useMemo(
        () => monitoringOptions.find((option) => option.id === selectedMonitoringId) || null,
        [monitoringOptions, selectedMonitoringId]
    );
    const filteredMonitoringOptions = useMemo(() => {
        const query = monitoringSearch.trim().toLowerCase();
        if (!query) return monitoringOptions;
        return monitoringOptions.filter((option) =>
            [option.websiteUrl, option.id, option.formFactor || 'mobile']
                .join(' ')
                .toLowerCase()
                .includes(query)
        );
    }, [monitoringOptions, monitoringSearch]);

    useEffect(() => {
        if (!manualSuggestionId) return;
        const hasManualSuggestion = normalizedSuggestions.some((suggestion) => suggestion.id === manualSuggestionId);
        if (!hasManualSuggestion) {
            setManualSuggestionId('');
        }
    }, [manualSuggestionId, normalizedSuggestions]);

    useEffect(() => {
        if (effectiveScopeAnswers?.goal && !isAdvancedExpanded) {
            setIsAdvancedExpanded(true);
        }
    }, [effectiveScopeAnswers?.goal, isAdvancedExpanded]);

    const selectedGoalCategory = isGoalCategory(effectiveScopeAnswers?.goal)
        ? effectiveScopeAnswers.goal
        : null;

    const preferredCategory = useMemo<PromptSpecialization>(() => {
        if (selectedGoalCategory && availableCategories.includes(selectedGoalCategory)) {
            return selectedGoalCategory;
        }
        if (availableCategories.includes(specialization)) {
            return specialization;
        }
        if (availableCategories.length > 0) {
            return availableCategories[0];
        }
        return 'mixed';
    }, [availableCategories, selectedGoalCategory, specialization]);

    const deterministicSuggestion = useMemo(() => {
        const preferred = suggestionsByCategory[preferredCategory] || [];
        return preferred[0] || normalizedSuggestions[0] || null;
    }, [normalizedSuggestions, preferredCategory, suggestionsByCategory]);

    const activeSuggestion = useMemo(() => {
        const manual = normalizedSuggestions.find((suggestion) => suggestion.id === manualSuggestionId);
        return manual || deterministicSuggestion;
    }, [deterministicSuggestion, manualSuggestionId, normalizedSuggestions]);

    const activeCategory: PromptSpecialization = activeSuggestion?.category || preferredCategory;

    const alternativeSuggestions = useMemo(
        () => normalizedSuggestions.filter((suggestion) => suggestion.id !== activeSuggestion?.id),
        [activeSuggestion?.id, normalizedSuggestions]
    );

    const togglePromptExpansion = useCallback((suggestionId: string) => {
        setExpandedPromptById((current) => ({
            ...current,
            [suggestionId]: !current[suggestionId]
        }));
    }, []);

    const updateScopeAnswers = useCallback(
        (patch: Partial<AgentScopeAnswers>) => {
            const next: AgentScopeAnswers = {
                ...effectiveScopeAnswers,
                ...patch
            };
            if (!scopeAnswers) {
                setInternalScopeAnswers(next);
            }
            if (typeof patch.goal === 'string' && patch.goal.trim().length > 0 && !isAdvancedExpanded) {
                setIsAdvancedExpanded(true);
            }
            onScopeAnswersChange?.(next);
        },
        [effectiveScopeAnswers, isAdvancedExpanded, onScopeAnswersChange, scopeAnswers]
    );

    // TODO: Remove legacy layout mode values after all callers are migrated to `builder`.
    const resolvedLayoutMode: 'builder' = layoutMode === 'builder' ? 'builder' : 'builder';

    if (!activeSuggestion) return null;

    const promptValue = applyScopeAnswersToPrompt(activeSuggestion.value, effectiveScopeAnswers);
    const resolvedActiveSuggestion: PromptScopeSuggestion = {
        ...activeSuggestion,
        value: promptValue
    };
    const isPromptExpanded = !!expandedPromptById[activeSuggestion.id];
    const canExpandPrompt = promptValue.length > 180 || promptValue.split('\n').length > 5;

    return (
        <div
            data-layout-mode={resolvedLayoutMode}
            className={`rounded-lg border border-border/70 bg-muted/25 p-3 space-y-3 ${className}`}
        >
            <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-1 text-sm font-semibold">
                    <Wand2 className="h-4 w-4 text-primary" />
                    Add Scope To Start
                </div>
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${CATEGORY_STYLES[specialization]}`}>
                    {specialization === 'mixed' ? 'Goal required' : toTitleCase(specialization)}
                </span>
                {memoryDerived && (
                    <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                        Memory anchored
                    </span>
                )}
            </div>

            {reason && (
                <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 text-amber-600 dark:text-amber-300" />
                    <span className="text-amber-800 dark:text-amber-200">{reason}</span>
                </div>
            )}

            <div className="rounded-md border border-border/60 bg-background/70 p-2.5">
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Goal</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {goalOptions.map((goal) => {
                        const selected = effectiveScopeAnswers?.goal === goal;
                        return (
                            <button
                                key={`goal-${goal}`}
                                type="button"
                                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                    selected
                                        ? 'border-primary/70 bg-primary/10 text-primary'
                                        : 'border-border/70 text-muted-foreground hover:border-border'
                                }`}
                                onClick={() => {
                                    updateScopeAnswers({
                                        goal: selected ? undefined : goal
                                    });
                                }}
                            >
                                {toTitleCase(goal)}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-2 flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">
                        Active suggestion: <span className="font-medium text-foreground">{toTitleCase(activeCategory)}</span>
                    </span>
                    <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary"
                        aria-expanded={isAdvancedExpanded}
                        onClick={() => setIsAdvancedExpanded((current) => !current)}
                    >
                        {isAdvancedExpanded ? 'Hide advanced' : 'Advanced'}
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isAdvancedExpanded ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                {isAdvancedExpanded && (
                    <div className="mt-2 space-y-2 border-t border-border/40 pt-2">
                        {targetAreaOptions.length > 0 && (
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Target</p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {targetAreaOptions.map((targetArea) => {
                                        const selected = effectiveScopeAnswers?.targetArea === targetArea;
                                        return (
                                            <button
                                                key={`target-${targetArea}`}
                                                type="button"
                                                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                                    selected
                                                        ? 'border-primary/70 bg-primary/10 text-primary'
                                                        : 'border-border/70 text-muted-foreground hover:border-border'
                                                }`}
                                                onClick={() => {
                                                    updateScopeAnswers({
                                                        targetArea: selected ? undefined : targetArea
                                                    });
                                                }}
                                            >
                                                {targetArea}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div>
                            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Verify</p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {verificationOptions.map((verification) => {
                                    const selected = effectiveScopeAnswers?.verification === verification;
                                    return (
                                        <button
                                            key={`verify-${verification}`}
                                            type="button"
                                            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                                selected
                                                    ? 'border-primary/70 bg-primary/10 text-primary'
                                                    : 'border-border/70 text-muted-foreground hover:border-border'
                                            }`}
                                            onClick={() => {
                                                updateScopeAnswers({
                                                    verification: selected ? undefined : verification
                                                });
                                            }}
                                        >
                                            {verification}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        {showMonitoringControls && (
                            <div>
                                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                    Monitoring baseline (optional)
                                    {hasMonitoringOptions ? ` - ${monitoringOptions.length} available` : ''}
                                </p>
                                {hasMonitoringOptions ? (
                                    <>
                                        <p className="mt-1 text-[11px] text-muted-foreground">
                                            Optional: attach one baseline for this run.
                                        </p>
                                        <button
                                            type="button"
                                            className="mt-1.5 flex w-full items-center justify-between rounded-md border border-border/70 bg-background px-2 py-1.5 text-left text-xs transition-colors hover:border-border"
                                            aria-expanded={isMonitoringPickerOpen}
                                            onClick={() =>
                                                setIsMonitoringPickerOpen((current) => !current)
                                            }
                                            disabled={disabled}
                                        >
                                            <span className="truncate pr-2">
                                                {selectedMonitoringOption
                                                    ? `${selectedMonitoringOption.websiteUrl} (${selectedMonitoringOption.formFactor || 'mobile'})`
                                                    : 'None'}
                                            </span>
                                            <ChevronDown
                                                className={`h-3.5 w-3.5 shrink-0 transition-transform ${
                                                    isMonitoringPickerOpen ? 'rotate-180' : ''
                                                }`}
                                            />
                                        </button>
                                        {isMonitoringPickerOpen && (
                                            <div className="mt-1.5 rounded-md border border-border/70 bg-background p-1">
                                                <input
                                                    type="text"
                                                    value={monitoringSearch}
                                                    onChange={(event) => setMonitoringSearch(event.target.value)}
                                                    placeholder="Search baselines by URL, id, or form factor"
                                                    className="w-full rounded border border-border/70 bg-background px-2 py-1.5 text-xs"
                                                />
                                                <div className="mt-1 max-h-40 overflow-auto">
                                                    <button
                                                        type="button"
                                                        className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs transition-colors ${
                                                            !selectedMonitoringId
                                                                ? 'bg-primary/10 text-primary'
                                                                : 'text-muted-foreground hover:bg-muted'
                                                        }`}
                                                        aria-pressed={!selectedMonitoringId}
                                                        onClick={() => {
                                                            onSelectMonitoring?.(null);
                                                            setIsMonitoringPickerOpen(false);
                                                        }}
                                                    >
                                                        <span>None</span>
                                                        {!selectedMonitoringId && <CheckCircle2 className="h-3.5 w-3.5" />}
                                                    </button>
                                                    {filteredMonitoringOptions.length > 0 ? (
                                                        filteredMonitoringOptions.map((option) => {
                                                            const selected = selectedMonitoringId === option.id;
                                                            return (
                                                                <button
                                                                    key={`monitoring-${option.id}`}
                                                                    type="button"
                                                                    className={`mt-1 flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs transition-colors ${
                                                                        selected
                                                                            ? 'bg-primary/10 text-primary'
                                                                            : 'text-muted-foreground hover:bg-muted'
                                                                    }`}
                                                                    aria-pressed={selected}
                                                                    onClick={() => {
                                                                        onSelectMonitoring?.(option);
                                                                        setIsMonitoringPickerOpen(false);
                                                                    }}
                                                                    title={option.id}
                                                                >
                                                                    <span className="truncate pr-2">
                                                                        {option.websiteUrl} ({option.formFactor || 'mobile'})
                                                                    </span>
                                                                    {selected && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
                                                                </button>
                                                            );
                                                        })
                                                    ) : (
                                                        <p className="px-2 py-1.5 text-xs text-muted-foreground">
                                                            No baselines match your search.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="mt-1 text-[11px] text-muted-foreground">
                                        No monitoring baselines found for this account.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="rounded-md border border-border/60 bg-background/70 p-2.5">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <p className="text-sm font-medium">{activeSuggestion.label}</p>
                        <p className="mt-0.5 text-[11px] text-muted-foreground">{activeSuggestion.rationale}</p>
                    </div>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${CATEGORY_STYLES[activeCategory]}`}>
                        {toTitleCase(activeCategory)}
                    </span>
                </div>

                <div className="relative mt-2">
                    <pre
                        className={[
                            'whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-[11px] leading-4 text-muted-foreground transition-all',
                            isPromptExpanded ? 'max-h-96 overflow-auto' : 'max-h-24 overflow-hidden'
                        ].join(' ')}
                    >
                        {promptValue}
                    </pre>
                    {!isPromptExpanded && canExpandPrompt && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-7 rounded-b-md bg-gradient-to-t from-muted/70 to-transparent" />
                    )}
                </div>

                {canExpandPrompt && (
                    <button
                        type="button"
                        className="mt-1.5 text-[11px] font-medium text-primary underline-offset-2 hover:underline"
                        onClick={() => togglePromptExpansion(activeSuggestion.id)}
                    >
                        {isPromptExpanded ? 'Collapse prompt' : 'Show full prompt'}
                    </button>
                )}

                <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2.5 text-xs"
                        disabled={disabled}
                        onClick={() => onUsePrompt(resolvedActiveSuggestion)}
                    >
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                        Use prompt
                    </Button>
                    {onUseAndRun && (
                        <Button
                            size="sm"
                            className="h-7 px-2.5 text-xs"
                            disabled={disabled}
                            onClick={() => onUseAndRun(resolvedActiveSuggestion)}
                        >
                            <Play className="mr-1 h-3.5 w-3.5" />
                            Use and run
                        </Button>
                    )}
                </div>
            </div>

            {alternativeSuggestions.length > 0 && (
                <div className="rounded-md border border-border/60 bg-background/70 p-2.5">
                    <button
                        type="button"
                        className="flex w-full items-center justify-between text-left"
                        aria-expanded={isAlternativesExpanded}
                        onClick={() => setIsAlternativesExpanded((current) => !current)}
                    >
                        <p className="text-xs font-medium text-foreground">
                            {isAlternativesExpanded
                                ? 'Hide alternatives'
                                : `Show alternatives (${alternativeSuggestions.length})`}
                        </p>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isAlternativesExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isAlternativesExpanded && (
                        <div className="mt-2 max-h-36 space-y-1 overflow-auto pr-1">
                            {alternativeSuggestions.map((suggestion) => {
                                const selected = manualSuggestionId === suggestion.id;
                                const category = suggestion.category || 'mixed';
                                return (
                                    <button
                                        key={`alternative-${suggestion.id}`}
                                        type="button"
                                        className={`flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                                            selected
                                                ? 'border-primary/60 bg-primary/5'
                                                : 'border-border/60 bg-background/50 hover:border-border'
                                        }`}
                                        onClick={() => setManualSuggestionId(suggestion.id)}
                                    >
                                        <span className="truncate pr-2">{suggestion.label}</span>
                                        <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${CATEGORY_STYLES[category]}`}>
                                            {toTitleCase(category)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {requiredHints.length > 0 && isAdvancedExpanded && (
                <div className="rounded-md border border-border/60 bg-background/60 p-2.5">
                    <button
                        type="button"
                        className="flex w-full items-center justify-between text-left"
                        onClick={() => setIsHintsExpanded((current) => !current)}
                        aria-expanded={isHintsExpanded}
                    >
                        <p className="text-xs font-medium text-foreground">Add at least one of these details</p>
                        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                            {isHintsExpanded ? 'Hide' : 'Show'}
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isHintsExpanded ? 'rotate-180' : ''}`} />
                        </span>
                    </button>
                    {isHintsExpanded && (
                        <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                            {requiredHints.slice(0, 4).map((hint, index) => (
                                <li key={`${hint}-${index}`} className="flex items-start gap-1.5">
                                    <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-primary/80" />
                                    <span>{hint}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
